// src/context/AuthContext.js
import React, { createContext, useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { useNavigate } from 'react-router-dom';

export const AuthContext = createContext();

// Lock the screen after this long with zero mouse/keyboard/touch/scroll
// activity. A session also gets locked (not logged out) the moment any API
// call comes back 401 - the backend's JWT has its own fixed 15-minute
// absolute expiry (JwtUtil.jwtExpirationMs) independent of activity, so
// someone continuously active for longer than that would otherwise hit an
// abrupt, unwarned logout with no relation to the idle timer at all.
const IDLE_TIMEOUT_MS = 3 * 60 * 1000;

// How long before the idle-lock a warning toast (with a live countdown and
// a "Stay Active" button) is shown, so the lock never comes as a total
// surprise. Any activity - including just moving the mouse over the toast -
// already resets the idle timer via the listeners below, so this is purely
// a heads-up; it doesn't change when the actual lock fires.
const IDLE_WARNING_MS = 30 * 1000;

export function AuthProvider({ children }) {
  const [user, setUserState] = useState(null);
  const [loading, setLoading] = useState(true);
  // Locking is NOT logging out: the token/user stay in localStorage exactly
  // as they were, the component tree never unmounts, and nothing navigates
  // away - so whatever the user was doing (an in-progress form, unsaved
  // input, scroll position) survives untouched. Unlocking just re-runs the
  // login call and swaps in a fresh token.
  const [locked, setLocked] = useState(false);
  const [unlockPassword, setUnlockPassword] = useState('');
  const [unlockError, setUnlockError] = useState('');
  const [unlocking, setUnlocking] = useState(false);
  const [idleWarning, setIdleWarning] = useState(false);
  const [idleSecondsLeft, setIdleSecondsLeft] = useState(0);
  const navigate = useNavigate();
  const userRef = useRef(user);

  useEffect(() => {
    userRef.current = user;
  }, [user]);

  // Helper to persist user in localStorage
  const persistUser = (userData) => {
    setUserState(userData);
    localStorage.setItem('user', JSON.stringify(userData));
  };

  // Fetches the full user record (name, profile picture, roles, etc.) by
  // username and persists it. This is the only place that ever learns
  // fullName - Login.js's own setUser call right after a fresh login only
  // has what the login response itself contains (username/role/id), so
  // without this being called there too, fullName would stay missing until
  // a full page reload happened to re-run the mount effect below.
  //
  // Asks for the signed-in user's own account (/api/auth/me). It used to
  // fetch /api/users and search it, which only returned the first 10
  // accounts (logging out anyone further down on every refresh) and is now
  // admin-only. The username check is a guard against a token/user mismatch.
  const refreshUserProfile = (username, onNotFound) => {
    return axios
      .get('/api/auth/me')
      .then((res) => {
        const matchedUser = res.data;
        if (matchedUser && matchedUser.username?.toLowerCase() === username.toLowerCase()) {
          const userData = {
            ...matchedUser,
            username: matchedUser.username.toLowerCase(),
            role: matchedUser.roles?.[0]?.name || 'examiner',
            id: matchedUser.id
          };
          persistUser(userData);
          return userData;
        }
        console.error("No matching user found for username:", username);
        if (onNotFound) onNotFound();
        return null;
      })
      .catch((err) => {
        console.error("Error fetching the signed-in user's profile:", err);
        if (onNotFound) onNotFound(err);
        return null;
      });
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      setUserState(JSON.parse(storedUser));
    }
    console.log("Token from localStorage:", token);
    if (token) {
      try {
        const decoded = jwtDecode(token);
        console.log("Decoded token:", decoded);
        const username = decoded.sub.toLowerCase();

        // Set the authorization header
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

        // A 401 here means the stored token has simply expired (most
        // commonly: a page refresh after the JWT's fixed 15-minute expiry
        // ran out while the screen was locked, or before it had a chance to
        // lock) - the storedUser above is already loaded, so lock in place
        // exactly as the runtime 401 interceptor below would, rather than
        // hard-logging-out and losing the "resume where I left off" flow.
        // Any other failure (network down, malformed token, deleted
        // account) still can't be fixed by re-entering a password, so it
        // keeps the hard logout.
        refreshUserProfile(username, (err) => {
          if (err?.response?.status === 401) {
            setLocked(true);
          } else {
            handleLogout();
          }
        }).finally(() => setLoading(false));
      } catch (error) {
        console.error("Error decoding token:", error);
        handleLogout();
      }
    } else {
      setUserState(null);
      delete axios.defaults.headers.common['Authorization'];
      setLoading(false);
    }
  }, []);

  // Any 401 means the current token is no longer usable (most commonly: the
  // backend's fixed 15-minute JWT expiry ran out) - lock in place rather
  // than logging out, so an actively-working user gets a re-auth prompt
  // instead of losing their page. Login attempts (wrong password, from the
  // lock screen or the real login page) also 401 - those must NOT trigger a
  // lock, they're handled by the caller's own error state.
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      response => response,
      error => {
        const isLoginCall = error.config?.url?.includes('/api/auth/login');
        if (error.response && error.response.status === 401 && !isLoginCall && userRef.current) {
          console.warn('Session expired - locking screen');
          setLocked(true);
        }
        return Promise.reject(error);
      }
    );
    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUserState(null);
    delete axios.defaults.headers.common['Authorization'];
    setLocked(false);
    setUnlockPassword('');
    setUnlockError('');
    navigate('/login', { replace: true });
  };

  const handleUnlockSubmit = async (e) => {
    e.preventDefault();
    setUnlocking(true);
    setUnlockError('');
    try {
      const res = await axios.post('/api/auth/login', {
        username: userRef.current?.username,
        password: unlockPassword
      });
      const { token } = res.data;
      if (!token) {
        setUnlockError('Incorrect password.');
        return;
      }
      localStorage.setItem('token', token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setUnlockPassword('');
      setLocked(false);
    } catch (err) {
      setUnlockError('Incorrect password.');
    } finally {
      setUnlocking(false);
    }
  };

  // Locks the screen after IDLE_TIMEOUT_MS of no mouse/keyboard/touch/
  // scroll activity. Deliberately stops listening while already locked -
  // activity on top of the blurred overlay (e.g. moving the mouse to reach
  // the password field) must never silently dismiss the lock on its own,
  // only a correct password (or the explicit "log out instead" escape
  // hatch) may do that.
  useEffect(() => {
    if (!user || locked) return undefined;

    let warnTimeoutId;
    let lockTimeoutId;
    let countdownIntervalId;

    const clearAll = () => {
      clearTimeout(warnTimeoutId);
      clearTimeout(lockTimeoutId);
      clearInterval(countdownIntervalId);
    };

    const resetTimer = () => {
      clearAll();
      setIdleWarning(false);
      warnTimeoutId = setTimeout(() => {
        setIdleWarning(true);
        setIdleSecondsLeft(Math.round(IDLE_WARNING_MS / 1000));
        countdownIntervalId = setInterval(() => {
          setIdleSecondsLeft((prev) => Math.max(0, prev - 1));
        }, 1000);
      }, IDLE_TIMEOUT_MS - IDLE_WARNING_MS);
      lockTimeoutId = setTimeout(() => {
        clearInterval(countdownIntervalId);
        setIdleWarning(false);
        setLocked(true);
      }, IDLE_TIMEOUT_MS);
    };

    const activityEvents = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart'];
    activityEvents.forEach(evt => window.addEventListener(evt, resetTimer));
    resetTimer();

    return () => {
      clearAll();
      activityEvents.forEach(evt => window.removeEventListener(evt, resetTimer));
    };
  }, [user, locked]);

  if (loading) {
    return <div>Loading...</div>;
  }

  const stayActive = () => {
    // Reuses the same activity listeners the idle timer already watches,
    // rather than threading a second reset function out of that effect.
    window.dispatchEvent(new Event('mousedown'));
  };

  return (
    <AuthContext.Provider value={{ user, setUser: persistUser, refreshUserProfile, logout: handleLogout, locked, lockSession: () => setLocked(true) }}>
      {children}
      {idleWarning && !locked && (
        <div
          style={{
            position: 'fixed',
            top: '1rem',
            right: '1rem',
            zIndex: 2100,
            background: '#fff',
            border: '1px solid #ffc107',
            borderRadius: 8,
            padding: '1rem 1.25rem',
            width: 'min(320px, calc(100vw - 2rem))',
            boxShadow: '0 8px 24px rgba(0,0,0,0.2)'
          }}
        >
          <div className="fw-bold mb-1">You're about to be locked out</div>
          <p className="text-muted small mb-2">
            No activity detected - you'll be locked out in {idleSecondsLeft}s due to inactivity.
          </p>
          <div className="d-flex gap-2">
            <button type="button" className="btn btn-primary btn-sm" onClick={stayActive}>
              Stay Active
            </button>
            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => { setIdleWarning(false); setLocked(true); }}>
              Lock now
            </button>
          </div>
        </div>
      )}
      {locked && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 2000,
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
            backgroundColor: 'rgba(5, 15, 94, 0.35)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '1rem'
          }}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: 8,
              padding: '2rem',
              width: 'min(360px, 100%)',
              boxShadow: '0 8px 30px rgba(0,0,0,0.3)'
            }}
          >
            <h5 style={{ marginBottom: '0.5rem' }}>Session locked</h5>
            <p className="text-muted small">
              You've been inactive for a while. Enter your password to continue exactly where you left off.
            </p>
            <form onSubmit={handleUnlockSubmit} autoComplete="off">
              <input
                type="text"
                value={user?.username || ''}
                disabled
                className="form-control mb-2"
                autoComplete="off"
                data-lpignore="true"
                data-1p-ignore="true"
                data-form-type="other"
              />
              <input
                type="password"
                placeholder="Password"
                value={unlockPassword}
                onChange={(e) => setUnlockPassword(e.target.value)}
                className="form-control mb-2"
                autoComplete="new-password"
                data-lpignore="true"
                data-1p-ignore="true"
                data-form-type="other"
                autoFocus
                required
              />
              {unlockError && <div className="text-danger small mb-2">{unlockError}</div>}
              <button type="submit" className="btn btn-primary w-100" disabled={unlocking}>
                {unlocking ? 'Unlocking...' : 'Unlock'}
              </button>
            </form>
            <button
              type="button"
              className="btn btn-link btn-sm mt-2 p-0"
              onClick={handleLogout}
            >
              Not you? Log out
            </button>
          </div>
        </div>
      )}
    </AuthContext.Provider>
  );
}
